// Kata Coaching store using Zustand for KATA Dashboard

import { create } from 'zustand';
import { KataCoachingSession, KataCoachingFilters, WorkshopWithUsers } from '../services/kataCoaching';
import { KataCoachingService } from '../services/kataCoaching';

interface KataCoachingState {
  // Data
  sessions: KataCoachingSession[];
  currentSession: KataCoachingSession | null;
  totalCount: number;
  
  // UI State
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  error: string | null;
  
  // Form data
  formData: Partial<KataCoachingSession>;
  
  // Pagination
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  
  // Filters
  filters: KataCoachingFilters;
}

interface KataCoachingActions {
  // Data fetching
  fetchSessions: (filters?: KataCoachingFilters) => Promise<void>;
  fetchSession: (id: number) => Promise<void>;
  refreshSessions: () => Promise<void>;
  
  // CRUD operations
  createSession: () => Promise<void>;
  updateSession: (id: number) => Promise<void>;
  deleteSession: (id: number) => Promise<void>;
  
  // Form management
  setFormData: (data: Partial<KataCoachingSession>) => void;
  resetForm: () => void;
  
  // Auto-fill functionality
  selectWorkshop: (workshopId: number) => Promise<void>;
  autoFillFromWorkshop: (workshop: WorkshopWithUsers) => void;
  
  // Calculations
  calculateTotalScore: () => number;
  calculateTotalScorePercentage: () => number;
  calculateCompletionRate: () => number;
  
  // Filters and pagination
  setFilters: (filters: Partial<KataCoachingFilters>) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  
  // UI state
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clearCurrentSession: () => void;
}

type KataCoachingStore = KataCoachingState & KataCoachingActions;

const getTodayDateString = () => new Date().toISOString().split('T')[0];

const STORAGE_KEY_FROM = 'kata_deadline_from';
const STORAGE_KEY_TO = 'kata_deadline_to';
const EVENT_NAME = 'kata_date_filter_change';
const STORAGE_KEY_ALV_ID = 'kata_plant_filter';
const PLANT_EVENT_NAME = 'kata_plant_filter_change';

const initialFilters: KataCoachingFilters = {
  page: 1,
  page_size: 20,
  ordering: '-coaching_date',
  deadline_from: localStorage.getItem(STORAGE_KEY_FROM) || undefined,
  deadline_to: localStorage.getItem(STORAGE_KEY_TO) || undefined,
  alv_id: localStorage.getItem(STORAGE_KEY_ALV_ID) || undefined
};

export const useKataCoachingStore = create<KataCoachingStore>((set, get) => {
  // Listen for custom events to sync across stores
  if (typeof window !== 'undefined') {
    window.addEventListener(EVENT_NAME, (e: any) => {
      const { startDate, endDate } = e.detail;
      const { filters } = get();
      
      // Only update if values are different
      if (startDate !== filters.deadline_from || endDate !== filters.deadline_to) {
        const updatedFilters = { 
          ...filters, 
          deadline_from: startDate, 
          deadline_to: endDate,
          page: 1 
        };
        set({ filters: updatedFilters, currentPage: 1 });
        // Automatically fetch new data when sync occurs
        get().fetchSessions(updatedFilters);
      }
    });

        window.addEventListener(PLANT_EVENT_NAME, (e: any) => {
          const { alvId } = e.detail;
          const { filters } = get();
          
          if (alvId !== filters.alv_id) {
            const updatedFilters = { ...filters, alv_id: alvId };
            set({ filters: updatedFilters, currentPage: 1 });
            get().fetchSessions(updatedFilters);
          }
        });
      }

  return {
  // Initial state
  sessions: [],
  currentSession: null,
  totalCount: 0,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  error: null,
  formData: { coaching_date: getTodayDateString() },
  currentPage: 1,
  pageSize: 20,
  hasNext: false,
  hasPrevious: false,
  filters: initialFilters,

  // Set loading state
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  // Set error message
  setError: (error: string | null) => {
    set({ error });
  },

  // Clear error message
  clearError: () => {
    set({ error: null });
  },

  // Clear current session
  clearCurrentSession: () => {
    set({ currentSession: null });
  },

  // Set form data
  setFormData: (data: Partial<KataCoachingSession>) => {
    const { formData } = get();
    set({ formData: { ...formData, ...data } });
  },

  // Reset form
  resetForm: () => {
    set({ formData: { coaching_date: getTodayDateString() } });
  },

  // Set filters
  setFilters: (newFilters: Partial<KataCoachingFilters>) => {
    const { filters } = get();
    
    // Check if dates are being updated
    let dateChanged = false;
    let newStartDate = filters.deadline_from;
    let newEndDate = filters.deadline_to;

    // Update local storage if deadlines are changed
    if (newFilters.deadline_from !== undefined) {
      dateChanged = true;
      newStartDate = newFilters.deadline_from;
      if (newFilters.deadline_from) {
        localStorage.setItem(STORAGE_KEY_FROM, newFilters.deadline_from);
      } else {
        localStorage.removeItem(STORAGE_KEY_FROM);
      }
    }
    
    if (newFilters.deadline_to !== undefined) {
      dateChanged = true;
      newEndDate = newFilters.deadline_to;
      if (newFilters.deadline_to) {
        localStorage.setItem(STORAGE_KEY_TO, newFilters.deadline_to);
      } else {
        localStorage.removeItem(STORAGE_KEY_TO);
      }
    }

    // Dispatch event if dates changed
    if (dateChanged) {
      const event = new CustomEvent(EVENT_NAME, { 
        detail: { startDate: newStartDate, endDate: newEndDate } 
      });
      window.dispatchEvent(event);
    }

    const updatedFilters = { ...filters, ...newFilters, page: 1 }; // Reset to page 1 when filtering
    set({ filters: updatedFilters, currentPage: 1 });
  },

  // Clear all filters
  clearFilters: () => {
    localStorage.removeItem(STORAGE_KEY_FROM);
    localStorage.removeItem(STORAGE_KEY_TO);
    
    // Dispatch clear event
    const event = new CustomEvent(EVENT_NAME, { 
      detail: { startDate: undefined, endDate: undefined } 
    });
    window.dispatchEvent(event);

    set({ filters: { ...initialFilters, deadline_from: undefined, deadline_to: undefined }, currentPage: 1 });
  },

  // Set current page
  setPage: (page: number) => {
    const { filters } = get();
    set({ 
      filters: { ...filters, page },
      currentPage: page 
    });
  },

  // Set page size
  setPageSize: (pageSize: number) => {
    const { filters } = get();
    set({ 
      filters: { ...filters, page_size: pageSize, page: 1 },
      pageSize,
      currentPage: 1 
    });
  },

  // Fetch sessions with current filters
  fetchSessions: async (customFilters?: KataCoachingFilters) => {
    const { filters, setLoading, setError, clearError } = get();
    
    try {
      setLoading(true);
      clearError();
      
      const filtersToUse = customFilters || filters;
      const response = await KataCoachingService.getSessions(filtersToUse);
      
      set({
        sessions: response.results,
        totalCount: response.count,
        hasNext: !!response.next,
        hasPrevious: !!response.previous,
        currentPage: filtersToUse.page || 1
      });
      
    } catch (error: any) {
      setError(error.message || 'Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  },

  // Refresh sessions with current filters
  refreshSessions: async () => {
    const { fetchSessions, filters } = get();
    await fetchSessions(filters);
  },

  // Fetch single session
  fetchSession: async (id: number) => {
    const { setLoading, setError, clearError } = get();
    
    try {
      setLoading(true);
      clearError();
      
      const session = await KataCoachingService.getSession(id);
      set({ currentSession: session });
      
    } catch (error: any) {
      setError(error.message || 'Failed to fetch session');
    } finally {
      setLoading(false);
    }
  },

  // Create new session
  createSession: async () => {
    const { formData, setError, clearError, refreshSessions, resetForm } = get();
    
    try {
      set({ isCreating: true });
      clearError();
      
      // Validate form data
      const errors = KataCoachingService.validateSession(formData);
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }
      
      await KataCoachingService.createSession(formData as KataCoachingSession);
      
      // Refresh the list to include the new session
      await refreshSessions();
      
      // Reset form
      resetForm();
      
    } catch (error: any) {
      setError(error.message || 'Failed to create session');
    } finally {
      set({ isCreating: false });
    }
  },

  // Update session
  updateSession: async (id: number) => {
    const { formData, setError, clearError, refreshSessions, currentSession } = get();
    
    try {
      set({ isUpdating: true });
      clearError();
      
      // Validate form data
      const errors = KataCoachingService.validateSession(formData);
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }
      
      const updatedSession = await KataCoachingService.updateSession(id, formData);
      
      // Update current session if it's the one being updated
      if (currentSession && currentSession.id === id) {
        set({ currentSession: updatedSession });
      }
      
      // Refresh the list
      await refreshSessions();
      
    } catch (error: any) {
      setError(error.message || 'Failed to update session');
    } finally {
      set({ isUpdating: false });
    }
  },

  // Delete session
  deleteSession: async (id: number) => {
    const { setError, clearError, refreshSessions, currentSession } = get();
    
    try {
      set({ isLoading: true });
      clearError();
      
      await KataCoachingService.deleteSession(id);
      
      // Clear current session if it's the one being deleted
      if (currentSession && currentSession.id === id) {
        set({ currentSession: null });
      }
      
      // Refresh the list
      await refreshSessions();
      
    } catch (error: any) {
      setError(error.message || 'Failed to delete session');
    } finally {
      set({ isLoading: false });
    }
  },

  // Auto-fill functionality
  selectWorkshop: async (workshopId: number) => {
    const { setLoading, setError, clearError, autoFillFromWorkshop } = get();
    try {
      setLoading(true);
      clearError();
      
      const workshop = await KataCoachingService.getWorkshopWithUsers(workshopId);
      
      // Auto-fill coach/mentor and mentees
      autoFillFromWorkshop(workshop);
      
    } catch (error: any) {
      setError(error.message || 'Failed to load workshop details');
    } finally {
      setLoading(false);
    }
  },

  autoFillFromWorkshop: (workshop: WorkshopWithUsers) => {
    const { formData } = get();
    const updatedFormData = { ...formData };
    
    // Auto-fill workshop
    updatedFormData.workshop = workshop.id;
    
    // Auto-fill coach/mentor if available
    if (workshop.mentor_user) {
      updatedFormData.coach_mentor = workshop.mentor_user.id;
    }
    
    // Auto-fill mentees if available
    if (workshop.mentees_users && workshop.mentees_users.length > 0) {
      updatedFormData.mentees = workshop.mentees_users.map(user => user.id);
    }
    
    set({ formData: updatedFormData });
  },

  // Calculate total score
  calculateTotalScore: () => {
    const { formData } = get();
    return KataCoachingService.calculateTotalScore(formData);
  },

  // Calculate total score percentage
  calculateTotalScorePercentage: () => {
    const { formData } = get();
    return KataCoachingService.calculateTotalScorePercentage(formData);
  },

  // Calculate completion rate
  calculateCompletionRate: () => {
    const { formData } = get();
    return KataCoachingService.calculateCompletionRate(formData);
  },
  };
});

// Utility hooks for common kata coaching operations
export const useKataCoachingSessions = () => {
  const { sessions, isLoading, error, totalCount, hasNext, hasPrevious } = useKataCoachingStore();
  return { sessions, isLoading, error, totalCount, hasNext, hasPrevious };
};

export const useCurrentKataCoachingSession = () => {
  const { currentSession, isLoading, error } = useKataCoachingStore();
  return { session: currentSession, isLoading, error };
};

export const useKataCoachingForm = () => {
  const { formData, setFormData, resetForm, calculateTotalScore, calculateTotalScorePercentage, calculateCompletionRate } = useKataCoachingStore();
  return { formData, setFormData, resetForm, calculateTotalScore, calculateTotalScorePercentage, calculateCompletionRate };
};

export const useKataCoachingFilters = () => {
  const { filters, setFilters, clearFilters } = useKataCoachingStore();
  return { filters, setFilters, clearFilters };
};
